/* ==========================================================================
   MONELI motion core — data-attribute driven, SPA-safe.
   initMotion(root) scans the DOM once and returns a cleanup that reverts
   EVERYTHING it did (attributes, split text, clones, listeners, tweens).

   Contract
   - Nothing is ever hidden unless JS armed it (data-rv="0"). No JS / errors
     => everything visible. A fail-safe reveals anything still armed.
   - prefers-reduced-motion => nothing is armed, split, pinned or moved.
   - Split text and marquee clones are restored on cleanup so React's DOM
     references stay valid.
   See _redesign/DESIGN-SYSTEM.md for the attribute reference.
   ========================================================================== */
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { prefersReducedMotion, hasFinePointer } from './api';

gsap.registerPlugin(ScrollTrigger);

export const REVEAL_SEL = [
  '[data-reveal]',
  '[data-aos]',
  '[data-reveal-stagger]',
  '[data-reveal-img]',
  '[data-split]',
  '[data-reveal-line]',
].join(',');

const lerp = (a, b, t) => a + (b - a) * t;
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const $$ = (sel, root) => Array.prototype.slice.call((root || document).querySelectorAll(sel));

/* ------------------------------------------------------------ split text */
function splitInto(node, frag, counter) {
  if (node.nodeType === 3) {
    node.textContent.split(/(\s+)/).forEach((part) => {
      if (!part) return;
      if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(' ')); return; }
      const w = document.createElement('span');
      w.className = 'split-w';
      const i = document.createElement('span');
      i.className = 'split-w__i';
      i.style.setProperty('--wi', counter.n++);
      i.textContent = part;
      w.appendChild(i);
      frag.appendChild(w);
    });
  } else if (node.nodeType === 1) {
    if (node.tagName === 'BR' || node.hasAttribute('data-split-skip') || node.tagName === 'svg' || node.tagName === 'SVG') {
      frag.appendChild(node.cloneNode(true));
      return;
    }
    const clone = node.cloneNode(false);
    Array.prototype.slice.call(node.childNodes).forEach((c) => splitInto(c, clone, counter));
    frag.appendChild(clone);
  }
}

function splitHeading(el) {
  if (el.__mSplit) return null;
  const originals = Array.prototype.slice.call(el.childNodes);
  const label = el.textContent.replace(/\s+/g, ' ').trim();
  const frag = document.createDocumentFragment();
  const counter = { n: 0 };
  originals.forEach((c) => splitInto(c, frag, counter));
  // Keep the original nodes (React holds references to them) and swap them out.
  originals.forEach((c) => el.removeChild(c));
  el.appendChild(frag);
  el.__mSplit = true;
  const hadLabel = el.hasAttribute('aria-label');
  if (!hadLabel && label) el.setAttribute('aria-label', label);
  // hide the per-word spans from AT; the heading keeps its accessible name
  Array.prototype.forEach.call(el.children, (c) => c.setAttribute('aria-hidden', 'true'));
  return () => {
    while (el.firstChild) el.removeChild(el.firstChild);
    originals.forEach((c) => el.appendChild(c));
    if (!hadLabel) el.removeAttribute('aria-label');
    el.__mSplit = false;
  };
}

/* --------------------------------------------------------------- reveals */
function armReveal(el) {
  if (el.hasAttribute('data-rv')) return;
  const d = el.getAttribute('data-delay');
  const aosDelay = el.getAttribute('data-aos-delay');
  if (d) el.style.setProperty('--delay', `${parseFloat(d)}s`);
  else if (aosDelay) el.style.setProperty('--delay', `${Math.min(parseFloat(aosDelay) / 1000, 0.8)}s`);
  if (el.hasAttribute('data-reveal-stagger')) {
    const st = el.getAttribute('data-reveal-stagger');
    if (st && !Number.isNaN(parseFloat(st))) el.style.setProperty('--stagger', `${parseFloat(st)}s`);
    Array.prototype.forEach.call(el.children, (c, i) => c.style.setProperty('--si', i));
  }
  el.setAttribute('data-rv', '0');
}

function disarmReveal(el) {
  el.removeAttribute('data-rv');
  el.style.removeProperty('--delay');
  el.style.removeProperty('--stagger');
  if (el.hasAttribute('data-reveal-stagger')) Array.prototype.forEach.call(el.children, (c) => c.style.removeProperty('--si'));
}

/* ------------------------------------------------------------------ main */
export function initMotion(root = document.body) {
  const cleanups = [];
  const add = (fn) => fn && cleanups.push(fn);
  const RM = prefersReducedMotion();
  const FINE = hasFinePointer();

  const ctx = gsap.context(() => {}, root);
  add(() => ctx.revert());

  /* ---- split + reveals (skipped entirely with reduced motion) ---------- */
  if (!RM) {
    $$('[data-split]', root).forEach((el) => add(splitHeading(el)));

    const armed = new Set();
    const io = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting || en.boundingClientRect.bottom < 0) {
            en.target.setAttribute('data-rv', '1');
            io.unobserve(en.target);
          }
        });
      }, { rootMargin: '0px 0px -7% 0px', threshold: 0 })
      : null;

    const arm = (el) => {
      if (armed.has(el)) return;
      armed.add(el);
      armReveal(el);
      if (!io) { el.setAttribute('data-rv', '1'); return; }
      io.observe(el);
    };
    $$(REVEAL_SEL, root).forEach(arm);

    // New nodes rendered later (tabs, filters, async data) get armed too.
    const mo = new MutationObserver((muts) => {
      muts.forEach((m) => m.addedNodes.forEach((n) => {
        if (n.nodeType !== 1 || n.closest('.split-w')) return;
        if (n.matches && n.matches(REVEAL_SEL)) arm(n);
        if (n.querySelectorAll) $$(REVEAL_SEL, n).forEach(arm);
        if (n.matches && n.matches('[data-split]') && !n.__mSplit) add(splitHeading(n));
      }));
    });
    mo.observe(root, { childList: true, subtree: true });

    // Fail-safe: never leave on-screen content hidden.
    const failSafe = () => armed.forEach((el) => {
      if (el.getAttribute('data-rv') !== '0') return;
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > -1) el.setAttribute('data-rv', '1');
    });
    const t1 = setTimeout(failSafe, 2600);
    const iv = setInterval(failSafe, 1500);

    add(() => {
      clearTimeout(t1); clearInterval(iv);
      mo.disconnect();
      if (io) io.disconnect();
      armed.forEach(disarmReveal);
      armed.clear();
    });
  }

  /* ---- counters ---------------------------------------------------------- */
  if (!RM && 'IntersectionObserver' in window) {
    const els = $$('[data-count]', root);
    const textNode = (el) => {
      if (el.childNodes.length === 1 && el.firstChild.nodeType === 3) return el.firstChild;
      return null;
    };
    const originals = new Map();
    els.forEach((el) => {
      const tn = textNode(el);
      if (!tn) return;
      const raw = el.getAttribute('data-count');
      const target = parseFloat(raw);
      if (Number.isNaN(target)) return;
      const dec = el.hasAttribute('data-decimals') ? parseInt(el.getAttribute('data-decimals'), 10) : ((raw.split('.')[1] || '').length);
      originals.set(el, { tn, text: tn.nodeValue, target, dec });
      tn.nodeValue = (0).toFixed(dec);
    });
    const rafs = new Set();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        io.unobserve(en.target);
        const o = originals.get(en.target);
        if (!o) return;
        const dur = parseFloat(en.target.getAttribute('data-duration') || 2) * 1000;
        const t0 = performance.now();
        const step = (now) => {
          const p = clamp((now - t0) / dur, 0, 1);
          const e = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
          o.tn.nodeValue = p === 1 ? o.text : (o.target * e).toFixed(o.dec);
          if (p < 1) { const id = requestAnimationFrame(step); rafs.add(id); }
        };
        rafs.add(requestAnimationFrame(step));
      });
    }, { threshold: 0.4 });
    originals.forEach((_, el) => io.observe(el));
    add(() => {
      io.disconnect();
      rafs.forEach((id) => cancelAnimationFrame(id));
      originals.forEach((o) => { o.tn.nodeValue = o.text; });
    });
  }

  /* ---- marquee ----------------------------------------------------------- */
  $$('[data-marquee]', root).forEach((m) => {
    const track = m.querySelector('.marquee__track');
    if (!track) return;
    const clones = [];
    if (!RM) {
      const w = track.scrollWidth || 1;
      const need = Math.max(1, Math.ceil((m.offsetWidth * 1.2) / w));
      for (let i = 0; i < need; i += 1) {
        const c = track.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.setAttribute('inert', '');
        $$('a, button', c).forEach((a) => a.setAttribute('tabindex', '-1'));
        m.appendChild(c);
        clones.push(c);
      }
      const pxps = parseFloat(m.getAttribute('data-speed')) || 50;
      m.style.setProperty('--speed', `${Math.max(10, w / pxps).toFixed(1)}s`);
      if (m.getAttribute('data-direction') === 'reverse') m.classList.add('marquee--reverse');
      m.classList.add('is-ready');
    }
    let io;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver((en) => m.classList.toggle('is-paused', !en[0].isIntersecting));
      io.observe(m);
    }
    add(() => {
      if (io) io.disconnect();
      clones.forEach((c) => c.remove());
      m.classList.remove('is-ready', 'is-paused', 'marquee--reverse');
      m.style.removeProperty('--speed');
    });
  });

  if (RM) {
    return () => { for (let i = cleanups.length - 1; i >= 0; i -= 1) { try { cleanups[i](); } catch (e) { /* noop */ } } };
  }

  /* ---- GSAP: parallax, fade-out, pin ------------------------------------- */
  const mm = gsap.matchMedia();
  add(() => mm.revert());
  ctx.add(() => {
    $$('[data-parallax]', root).forEach((el) => {
      const speed = parseFloat(el.getAttribute('data-parallax')) || 0.15;
      const trigger = el.closest('[data-parallax-trigger]') || el.parentElement;
      gsap.fromTo(el, { yPercent: speed * 100 }, {
        yPercent: -speed * 100,
        ease: 'none',
        scrollTrigger: { trigger, start: 'top bottom', end: 'bottom top', scrub: true, invalidateOnRefresh: true },
      });
    });

    $$('[data-fade-out]', root).forEach((el) => {
      const trigger = el.closest('section, header') || el.parentElement;
      gsap.to(el, {
        opacity: 0.0,
        y: () => -(parseFloat(el.getAttribute('data-fade-out')) || 80),
        ease: 'none',
        scrollTrigger: { trigger, start: 'top top', end: 'bottom top', scrub: true, invalidateOnRefresh: true },
      });
    });

    mm.add('(min-width: 992px)', () => {
      $$('[data-pin]', root).forEach((el) => {
        const len = el.getAttribute('data-pin') || '100%';
        const navH = (document.querySelector('.site-nav') || { offsetHeight: 0 }).offsetHeight;
        ScrollTrigger.create({
          trigger: el,
          pin: true,
          start: `top top+=${el.hasAttribute('data-pin-top') ? 0 : navH}`,
          end: `+=${len}`,
          invalidateOnRefresh: true,
          onUpdate: (self) => el.style.setProperty('--progress', self.progress.toFixed(4)),
        });
      });
    });
  });

  /* ---- magnetic ---------------------------------------------------------- */
  if (FINE) {
    $$('[data-magnetic]', root).forEach((el) => {
      const strength = parseFloat(el.getAttribute('data-magnetic')) || 0.3;
      let mx = 0; let my = 0; let cx = 0; let cy = 0; let raf = null; let active = false;
      const loop = () => {
        cx = lerp(cx, mx, 0.16); cy = lerp(cy, my, 0.16);
        el.style.transform = `translate3d(${cx.toFixed(2)}px, ${cy.toFixed(2)}px, 0)`;
        if (active || Math.abs(cx - mx) > 0.1 || Math.abs(cy - my) > 0.1) raf = requestAnimationFrame(loop);
        else { raf = null; if (!active) el.style.transform = ''; }
      };
      const move = (e) => {
        const r = el.getBoundingClientRect();
        mx = (e.clientX - (r.left + r.width / 2)) * strength;
        my = (e.clientY - (r.top + r.height / 2)) * strength;
        active = true;
        if (!raf) raf = requestAnimationFrame(loop);
      };
      const leave = () => { mx = 0; my = 0; active = false; if (!raf) raf = requestAnimationFrame(loop); };
      el.addEventListener('pointermove', move);
      el.addEventListener('pointerleave', leave);
      add(() => {
        el.removeEventListener('pointermove', move);
        el.removeEventListener('pointerleave', leave);
        if (raf) cancelAnimationFrame(raf);
        el.style.transform = '';
      });
    });
  }

  return () => {
    for (let i = cleanups.length - 1; i >= 0; i -= 1) {
      try { cleanups[i](); } catch (e) { /* keep reverting */ }
    }
  };
}
